import { ESPLoader, Transport } from 'esptool-js';
import type { IEspLoaderTerminal } from 'esptool-js/lib/types/loaderTerminal';
import { USB_PORT_FILTERS } from './serial-port';

/**
 * esptool-js integration seam (package pinned at ^0.6.x).
 *
 * All esptool-js API usage is confined to this file so future version drift
 * has exactly one place to be fixed. Verified against esptool-js 0.6.1:
 * ESPLoader.main() connects and detects the chip, writeFlash() writes a
 * fileArray with per-call progress, after() performs the hard reset.
 */

export interface FlashImage {
  name: string;
  offset: number;
  dataBase64: string;
}

export interface EspFlasherOptions {
  baudRate: number;
  onLine: (line: string) => void;
  onProgress: (pct: number | null) => void;
}

export class EspFlasher {
  /**
   * Flashes the given images in offset order and returns the underlying port
   * so the caller can reopen it for the serial monitor.
   */
  async flash(images: FlashImage[], options: EspFlasherOptions): Promise<SerialPort> {
    if (images.length === 0) {
      throw new Error('The compiler did not return any flashable images.');
    }

    options.onProgress(null);
    options.onLine('Requesting serial port…');
    const port = await navigator.serial.requestPort(USB_PORT_FILTERS);

    const transport = new Transport(port);
    const terminal: IEspLoaderTerminal = {
      clean: () => {},
      writeLine: (data: string) => this.consumeEspLoaderLine(data, options),
      write: (data: string) => this.consumeEspLoaderLine(data, options),
    };
    const loader = new ESPLoader({
      transport,
      baudrate: options.baudRate,
      terminal,
      debugLogging: false,
    });

    options.onLine('Connecting to the bootloader…');
    const chip = await loader.main();
    options.onLine(`Connected: ${chip}.`);

    const ordered = [...images].sort((a, b) => a.offset - b.offset);
    const fileArray = ordered.map((image) => ({
      data: base64ToBytes(image.dataBase64),
      address: image.offset,
    }));
    const grandTotal = fileArray.reduce((sum, f) => sum + f.data.length, 0);
    let bytesBeforeCurrentFile = 0;
    let lastReported = -1;

    options.onLine(`Writing ${fileArray.length} image(s), ${Math.round(grandTotal / 1024)} KB total…`);
    await loader.writeFlash({
      fileArray,
      flashMode: 'dio',
      flashFreq: '40m',
      flashSize: '4MB',
      eraseAll: false,
      compress: true,
      reportProgress: (_fileIndex, written, _total) => {
        bytesBeforeCurrentFile =
          fileArray.slice(0, _fileIndex).reduce((sum, f) => sum + f.data.length, 0);
        const overall = (bytesBeforeCurrentFile + written) / grandTotal;
        const pct = Math.min(0.9999, overall);
        if (pct - lastReported > 0.005 || pct >= 0.9999) {
          lastReported = pct;
          options.onProgress(pct);
        }
      },
    });
    options.onProgress(null);

    await loader.after('hard_reset');
    options.onLine('Flash complete. Board reset.');

    try {
      await transport.disconnect();
    } catch {
      /* port may already be closed by the reset */
    }
    return port;
  }

  private consumeEspLoaderLine(raw: string, options: EspFlasherOptions): void {
    for (const line of raw.split(/\r\n|\n|\r/)) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      options.onLine(trimmed);
    }
  }
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
